import React, {useContext, useEffect, useRef, useState} from "react";
import {injectIntl} from "react-intl";
import {VaprDeviceOverviewContext} from "./context/VaprDeviceOverviewContext";
import {fetchVaprDeviceDetails} from "./network/VaprDeviceNetworkAPI";
import {getAssetRefreshInterval, sortComparator} from "../../util/Util";
import {ASCENDING} from "../../util/Constants";
import {FeatureUtility, FEATURES} from "../../util/FeatureUtility";
import {getLocalizedMessage} from "../../util/Localization";
import {checkDeviceMonitoringEnabled, getMonitoringStatusDetails} from "../../util/Validations";
import {PermissionsContext} from "../../permissions/PermissionsContext";
import AddVaprSingleDeviceModal from "./modal/AddVaprSingleDeviceModal";
import Loader from "@amzn/meridian/loader";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Box from "@amzn/meridian/box";
import Button from "@amzn/meridian/button";
import Tooltip from "@amzn/meridian/tooltip";
import Icon from "@amzn/meridian/icon";
import chevronDownLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-down-large";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import Alert from "@amzn/meridian/alert";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import RefreshTimer from "../../util/RefreshTimer";
import Text from "@amzn/meridian/text";

const VaprDeviceOverview = ({intl}) => {

    const {viewModel, dispatch} = useContext(VaprDeviceOverviewContext);
    const {isAuthorized} = useContext(PermissionsContext);
    const tableEndRef = useRef(null);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [selected, setSelected] = useState("");

    useEffect(() => {
        getData();
        const assetInterval = setInterval(() => {
            if (!document.hidden) {
                getData();
            }
        }, getAssetRefreshInterval());
        return () => clearInterval(assetInterval);
    }, []);

    const getData = () => {
        fetchVaprDeviceDetails(dispatch);
    }

    const scrollToBottom = () => {
        tableEndRef.current.scrollIntoView({behavior: "smooth"});
    }

    const onSort = (sortDetails) => {
        dispatch({
            type: 'SORT_DATA',
            data: {column: sortDetails.sortColumn, direction: sortDetails.sortDirection}
        });
    }

    const sortedDeviceList = Object.values(viewModel.vaprDeviceInformation);
    sortedDeviceList.sort((a, b) => {
        const {column, direction} = viewModel.sortPref;
        const compareValue = sortComparator(a[column], b[column]);
        return direction === ASCENDING ? compareValue : -compareValue;
    });

    if (viewModel.loadingOverview) {
        return (<Loader/>);
    }

    return (
        <Row widths="100%" alignmentVertical="top" spacingInset="none none large none">
            <Column>
                <Row width="100%" widths={["100", "fill", "100"]}>
                    <RefreshTimer deps={[viewModel]}/>
                    <Box></Box>
                    <Tooltip position="top" title={getLocalizedMessage({intl, stringId: "tooltip.scrolltobottom"})}>
                        <Button onClick={scrollToBottom} type="secondary" size="small">
                            <Icon tokens={chevronDownLargeTokens}/>
                        </Button>
                    </Tooltip>
                </Row>
                {sortedDeviceList.length === 0 ?
                    <Alert type="informational" size="small">
                        {getLocalizedMessage({intl, stringId: "alert.device"})}
                    </Alert> :
                    <Table
                        headerRows={1}
                        spacing="small"
                        sortColumn={viewModel.sortPref.column}
                        sortDirection={viewModel.sortPref.direction}
                        onSort={onSort}
                        showDividers={true}
                        showStripes={true}>
                        <TableRow highlightOnHover={false}>
                            <TableCell sortColumn="name">
                                {getLocalizedMessage({intl, stringId: "device.name"})}
                            </TableCell>
                            <TableCell sortColumn="mac">
                                {getLocalizedMessage({intl, stringId: "device.mac_ip"})}
                            </TableCell>
                            <TableCell>
                                <b>Connection Status</b>
                            </TableCell>
                            <TableCell>
                                <b>Monitoring</b>
                            </TableCell>
                            {FeatureUtility.getFeatureStatus(FEATURES.IS_VAPR_FOV_ENABLED) &&
                                <TableCell>
                                    <Tooltip position="top" title="D: Detection Field of View, P: Projection Field of View (Length × Breadth)">
                                        <b>FOV</b>
                                    </Tooltip>
                                </TableCell>}
                            <TableCell>
                                {getLocalizedMessage({intl, stringId: "device.action"})}
                            </TableCell>
                        </TableRow>
                        {sortedDeviceList.map((device) => (
                            <TableRow key={device.mac} highlightOnHover={true}>
                                <TableCell>
                                    <Tooltip position="top" title={`Device ID: ${device.deviceId}`}>
                                        <Column>
                                            <b>{device.name}</b>
                                            {device.deviceType}
                                        </Column>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Column>
                                        <b>{device.mac}</b>
                                        {device.ip}:{device.port}
                                    </Column>
                                </TableCell>
                                {/* TODO: Fetch connection status from network call (API TBD) */}
                                <TableCell>
                                    <Text color="secondary">Status Unavailable</Text>
                                </TableCell>
                                <TableCell>
                                    {(() => {
                                        const monitoring = checkDeviceMonitoringEnabled(Number(device.monitoringStartTime), Number(device.monitoringEndTime));
                                        const {monitoringStatusColor, monitoringStatusDefaultMessage} = getMonitoringStatusDetails(monitoring);
                                        return <b style={{color: monitoringStatusColor}}>{monitoringStatusDefaultMessage}</b>;
                                    })()}
                                </TableCell>
                                {FeatureUtility.getFeatureStatus(FEATURES.IS_VAPR_FOV_ENABLED) &&
                                    <TableCell>
                                        <Column>
                                            {device.detectionFovLength && device.detectionFovBreadth
                                                ? <span>D: {device.detectionFovLength} × {device.detectionFovBreadth} {device.detectionFovUnit}</span>
                                                : <span>D: -</span>}
                                            {device.projectionFovLength && device.projectionFovBreadth
                                                ? <span>P: {device.projectionFovLength} × {device.projectionFovBreadth} {device.projectionFovUnit}</span>
                                                : <span>P: -</span>}
                                        </Column>
                                    </TableCell>}
                                <TableCell>
                                    <Text color="secondary">-</Text>
                                </TableCell>
                            </TableRow>
                        ))}
                    </Table>
                }
                <Row width="100%" widths={["100", "fill"]}>
                    <Tooltip position="end" title={!isAuthorized ? getLocalizedMessage({intl, stringId: "tooltip.isAuthorized"}) : ""}>
                        <Button onClick={() => setOpenAddModal(true)} type="primary" size="small" disabled={!isAuthorized}>
                            <Icon tokens={plusCircleTokens}/>
                            {getLocalizedMessage({intl, stringId: "device.add_device_button_modal"})}
                        </Button>
                    </Tooltip>
                    <Box></Box>
                </Row>
                <AddVaprSingleDeviceModal open={openAddModal} setOpen={setOpenAddModal}
                                          selected={selected} setSelected={setSelected}/>
                <div ref={tableEndRef}/>
            </Column>
        </Row>
    );
}

export default injectIntl(VaprDeviceOverview)



// WEBPACK FOOTER //
// ./src/components/devicemanagement/vaprdevice/VaprDeviceOverview.js